/**
 * @fileoverview Componente Organismo para mostrar un modal lateral con respuestas rápidas adicionales asociadas a una respuesta principal.
 * Incluye renderizado de contenido multimedia y texto, con soporte para cierre y traducción.
 * Cumple con Atomic Design (Organism) y optimizaciones de renderizado (React.memo, useCallback, useMemo).
 *
 * @module MultipleFastAnswersModalMC
 * @category Component - Organism
 * @since 2.0.0
 * @version 3.1.0
 */

import React, { memo, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import RightModalMC from '../../components/RightModalMC';
import VideoMC from '../../components/VideoMC';
import ImageMC from '../../components/ImageMC';
import AudioMC from '../../components/AudioMC';
import FilePreviewMC from './FilePreviewMC';

/**
 * @typedef {Object} AdditionalFastAnswer
 * @description Estructura de una respuesta rápida adicional.
 * @property {string|number} id - Identificador único de la respuesta adicional.
 * @property {Object} attributes - Atributos de la respuesta adicional.
 * @property {string} attributes.answer - Contenido textual de la respuesta.
 * @property {string} attributes.file_type - Tipo de archivo asociado ('image', 'audio', 'video', 'file').
 * @property {string} attributes.file_url - URL del archivo asociado.
 */

/**
 * @typedef {Object} MultipleFastAnswersModalMCProps
 * @description Props del componente MultipleFastAnswersModalMC.
 * @property {boolean} showMultipleAnswers - Indica si el modal está abierto.
 * @property {Object} selectedAnswer - Respuesta principal seleccionada (puede ser null).
 * @property {function} toggleMultipleAnswersModal - Callback para abrir/cerrar el modal.
 */

/**
 * Componente Organismo para mostrar un modal lateral con respuestas rápidas adicionales.
 *
 * @component MultipleFastAnswersModalMC
 * @category Component - Organism
 *
 * @description
 * **Características:**
 * - Muestra un modal lateral con todas las respuestas adicionales asociadas a una respuesta rápida principal.
 * - Renderiza contenido multimedia (imagen, audio, video, archivo) y texto.
 * - Permite cerrar el modal mediante callback.
 * - Optimizado con React.memo, useCallback y useMemo.
 *
 * **Atomic Design:**
 * - **Nivel:** Organism
 * - **Composición:** Usa organismos (RightModalMC), átomos (ImageMC, AudioMC, VideoMC)
 *
 * @param {MultipleFastAnswersModalMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Modal lateral con respuestas adicionales
 *
 * @example
 * <MultipleFastAnswersModalMC
 *   showMultipleAnswers={true}
 *   selectedAnswer={selectedAnswer}
 *   toggleMultipleAnswersModal={fn}
 * />
 *
 * @see {@link RightModalMC} Modal lateral reutilizable
 * @see {@link ImageMC} Renderizado de imagen
 * @see {@link AudioMC} Renderizado de audio
 * @see {@link VideoMC} Renderizado de video
 *
 * @since 2.0.0
 * @version 3.1.0 - Documentación JSDoc completa y optimización de performance
 */

const MultipleFastAnswersModalMC = memo(({
  showMultipleAnswers,
  selectedAnswer,
  toggleMultipleAnswersModal
}) => {
  const { t } = useTranslation('FastAnswer');

  /**
   * Cierra el modal de respuestas múltiples.
   * @function handleClose
   * @memberof MultipleFastAnswersModalMC
   * @param {React.MouseEvent} e - Evento de cierre
   */
  const handleClose = useCallback((e) => {
    toggleMultipleAnswersModal(e, null);
  }, [toggleMultipleAnswersModal]);

  /**
   * Título del modal (nombre de la respuesta principal o texto por defecto).
   * @type {string}
   */
  const modalTitle = useMemo(() => {
    return selectedAnswer?.attributes.title || t('multipleAnswers');
  }, [selectedAnswer?.attributes.title, t]);

  /**
   * Lista de respuestas adicionales a mostrar.
   * @type {Array<AdditionalFastAnswer>}
   */
  const additionalAnswers = useMemo(() => {
    return selectedAnswer?.attributes.additional_fast_answers.data || [];
  }, [selectedAnswer?.attributes.additional_fast_answers?.data]);

  /**
   * Renderiza el contenido multimedia de una respuesta adicional.
   * @function renderFileContent
   * @memberof MultipleFastAnswersModalMC
   * @param {AdditionalFastAnswer} afa - Respuesta adicional
   * @returns {JSX.Element|null}
   */
  const renderFileContent = useCallback((afa) => {
    const { file_type, file_url, file_name } = afa.attributes;
    
    switch (file_type) {
      case 'image':
        return (
          <>
            <span className="tw-text-sm tw-text-blue-600 tw-font-medium tw-block tw-mb-1">{t('image')}</span>
            <ImageMC
              data={{ contentMediaUrl: file_url }}
              alt={t('imageAlt')}
              className="tw-w-48 tw-h-48 tw-object-cover tw-rounded tw-border"
            />
          </>
        );
      case 'audio':
        return (
          <>
            <span className="tw-text-sm tw-text-blue-600 tw-font-medium tw-block tw-mb-1">{t('audio')}</span>
            <AudioMC
              data={{ contentMediaUrl: file_url }}
              className="tw-w-48"
            />
          </>
        );
      case 'video':
        return (
          <>
            <span className="tw-text-sm tw-text-blue-600 tw-font-medium tw-block tw-mb-1">{t('video')}</span>
            <VideoMC
              data={{ contentMediaUrl: file_url }}
              className="tw-w-48 tw-h-48 tw-rounded tw-border"
            />
          </>
        );
      case 'file':
        return (
          <>
            <span className="tw-text-sm tw-text-blue-600 tw-font-medium tw-block tw-mb-1">{t('file')}</span>
            <FilePreviewMC 
              fileUrl={file_url} 
              fileName={file_name}
              fileType={file_type}
            />
          </>
        );
      default:
        return null;
    }
  }, [t]);

  return (
    <RightModalMC
      title={modalTitle}
      isOpen={showMultipleAnswers}
      onClose={handleClose}
      classContainer="!tw-z-[10020]"
      overlayClassName="!tw-z-[10019]"
    >
      <div className="tw-p-4 tw-overflow-y-auto tw-h-full">
        <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-4">{t('additionalAnswers')}</p>
        {additionalAnswers?.map((afa) => (
          <div key={afa.id} className="tw-mb-6">
            <span className="tw-text-sm tw-text-blue-600 tw-font-medium tw-block tw-mb-1">{t('content')}</span>
            <pre className="tw-text-xs tw-mb-4 tw-font-sans tw-whitespace-pre-wrap tw-text-gray-700 tw-bg-gray-50 tw-p-3 tw-rounded tw-border">
              {afa.attributes.answer}
            </pre>
            {renderFileContent(afa)}
            <div className="tw-border-b tw-border-gray-100 tw-mt-4"></div>
          </div>
        ))}
      </div>
    </RightModalMC>
  );
});

MultipleFastAnswersModalMC.displayName = 'MultipleFastAnswersModalMC';

export default MultipleFastAnswersModalMC;
